/**
 * Constantes partagées entre le client et le serveur de signaling.
 *
 * Ces valeurs sont le contrat de fait entre les deux côtés : les modifier d'un seul côté
 * casse silencieusement le transfert. Elles vivent donc dans le package partagé.
 */

/**
 * Taille d'un chunk de fichier envoyé sur le canal de données.
 *
 * La trame complète fait `CHUNK_SIZE + FRAME_HEADER_SIZE + TAG_BYTES` (cette dernière
 * borne est exportée comme `MAX_FRAME_SIZE`). Elle doit rester **strictement inférieure
 * à 65 536 octets**, la limite SCTP qu'un navigateur peut imposer sans la négocier :
 * Safari/WebKit jette silencieusement tout message au-delà, sans erreur ni fermeture
 * du canal. L'émetteur croit alors avoir envoyé un morceau que le récepteur n'a jamais
 * vu, la séquence diverge, et le transfert échoue avec une erreur d'intégrité.
 *
 * D'où 63 KiB et non 64 KiB : il faut de la place pour l'en-tête (21 octets) et le
 * tag d'authentification AES-GCM (16 octets).
 */
export const CHUNK_SIZE = 63 * 1024;

/**
 * Limite SCTP de fait : aucun message de data channel ne doit la dépasser.
 */
export const SCTP_SAFE_MESSAGE_LIMIT = 64 * 1024;

/**
 * Seuil haut de backpressure : au-delà, on cesse d'alimenter le canal et on attend
 * l'événement `bufferedamountlow`.
 *
 * C'est LE paramètre qui évite la saturation mémoire sur les gros fichiers. Sans lui,
 * `send()` en boucle accumule des centaines de Mo dans le buffer SCTP.
 */
export const HIGH_WATER = 4 * 1024 * 1024;

/** Seuil bas : on reprend l'alimentation une fois le buffer redescendu à ce niveau. */
export const LOW_WATER = 512 * 1024;

/** Longueur de l'identifiant de salle, en caractères base62 (~125 bits d'entropie). */
export const ROOM_ID_LENGTH = 21;

/** Taille de la clé AES-GCM, en octets. */
export const KEY_BYTES = 32;

/** Taille de l'IV AES-GCM, en octets. */
export const IV_BYTES = 12;

/** Taille du tag d'authentification AES-GCM, en octets. */
export const TAG_BYTES = 16;

/**
 * En-tête d'une trame du canal de données :
 * `[1B type][4B fileIndex][4B seq][12B iv]`.
 */
export const FRAME_HEADER_SIZE = 1 + 4 + 4 + IV_BYTES;

/** Trame transportant un message de contrôle (JSON scellé). */
export const FRAME_TYPE_CONTROL = 0x01;

/** Trame transportant un morceau de fichier scellé. */
export const FRAME_TYPE_CHUNK = 0x02;

/**
 * Taille maximale d'une trame complète (chunk plein + tag).
 *
 * Cette borne doit rester inférieure à `SCTP_SAFE_MESSAGE_LIMIT` : le vérifier au
 * démarrage (et non à l'exécution dans le navigateur, où le silence est total) est ce
 * qui empêche une régression de ce réglage de repasser inaperçue.
 */
export const MAX_FRAME_SIZE = FRAME_HEADER_SIZE + CHUNK_SIZE + TAG_BYTES;

if (MAX_FRAME_SIZE >= SCTP_SAFE_MESSAGE_LIMIT) {
  throw new Error(
    `CHUNK_SIZE produirait des trames de ${MAX_FRAME_SIZE} octets, au-delà de la ` +
      `limite SCTP de ${SCTP_SAFE_MESSAGE_LIMIT} qu'un navigateur peut imposer sans la ` +
      'négocier (Safari y abandonne les messages silencieusement).',
  );
}

/**
 * Au-delà de cette taille, la stratégie d'écriture `memory` est refusée en amont.
 *
 * Accumuler un fichier entier en `ArrayBuffer` est le comportement qui fait planter Safari
 * (constat documenté par ToffeeShare). On préfère refuser tôt, avec un message actionnable,
 * plutôt que d'échouer à 60 % du transfert.
 */
export const MEMORY_WRITE_LIMIT = 512 * 1024 * 1024;

/** Version du protocole applicatif. Permet un refus propre en cas d'incompatibilité. */
export const PROTOCOL_VERSION = 1;

/** Port par défaut du serveur de signaling. */
export const DEFAULT_SIGNAL_PORT = 8080;

/** Intervalle entre deux pings applicatifs côté client (mesure de latence). */
export const PING_INTERVAL_MS = 25_000;

/** Délai d'attente avant de considérer la connexion WebSocket comme morte. */
export const PONG_TIMEOUT_MS = 60_000;

/**
 * Délai après lequel une tentative de connexion P2P est déclarée en échec.
 *
 * 8 s suffisaient quand le relais de secours était un chemin viable. Il ne l'est pas
 * pour un gros fichier : le relais WebSocket plafonne autour de 40 Ko/s, donc un
 * transfert de 120 Mo y prend une heure. Mieux vaut laisser ICE aboutir (allocation
 * TURN + collecte + vérification des paires) que basculer trop tôt sur un chemin
 * condamné. Constaté le 22/10/2026 : ICE abandonné à 8 s, relais qui lâche à 1,2 %.
 */
export const ICE_FAILURE_TIMEOUT_MS = 20_000;

/** Débit observé du relais WebSocket, pour annoncer une durée honnête à l'utilisateur. */
export const RELAY_ASSUMED_BYTES_PER_SECOND = 40_000;

/**
 * Taille à partir de laquelle le passage au relais déclenche un avertissement chiffré.
 *
 * En dessous, le relais reste tolérable (quelques secondes à quelques minutes) ; au-delà,
 * l'utilisateur doit savoir ce qu'il accepte avant de laisser tourner l'onglet.
 */
export const RELAY_NOTICE_THRESHOLD_BYTES = 10 * 1024 * 1024;
